import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  Platform,
  ViewStyle,
} from 'react-native';
import { Image } from 'expo-image';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import { ClothingItem } from '../types';
import { Colors, Fonts, Radii, Shadows, Spacing, StatusTints, Typography } from '../constants';
import { imageService } from '../services/images';
import { formatRelativeTime } from '../utils/relativeTime';
import { ConditionBadge } from './ConditionBadge';

// ── Fixed sizes ──────────────────────────────────────────────────────────────
const COMPACT_THUMB = 56;
const COMPACT_PADDING_VERTICAL = 12;
const COMPACT_PRICE_SLOT = 80;

/**
 * Height of one hairline row (thumb + padding + the hairline) — usable for
 * `getItemLayout` in lists of compact cards.
 */
export const ITEM_CARD_COMPACT_HEIGHT = COMPACT_THUMB + COMPACT_PADDING_VERTICAL * 2;
/** Hairline between compact rows — the list renders it (see `ItemCardSeparator`). */
export const ITEM_CARD_SEPARATOR_HEIGHT = 1;

interface ItemCardProps {
  item: ClothingItem;
  onPress: () => void;
  /** Hairline row: 56px thumb, title + meta, status chip, price right */
  compact?: boolean;
  /** Mobile 2-column grid: 1:1 photo instead of 4:3 */
  square?: boolean;
  /** Distinct-viewer count, shown only on the owner's own listings. */
  viewCount?: number;
  style?: ViewStyle;
}

const price = (n: number) => `€${Number(n || 0).toFixed(2)}`;

const META_SEPARATOR = ' · ';

/**
 * Listing card (design v2) — naked: the card itself has no background, border
 * or shadow.
 *
 * Grid: the photo (4:3, or 1:1 with `square`) carries the only radius and the
 * only hover lift; under it the title, one meta line
 * ("Size 140 · VV Zwolle · 2 hours ago") and a footer with the bold green
 * price left and the tinted condition chip right.
 *
 * Compact: a hairline row — 56px thumb, title + meta, the status chip (or the
 * condition chip while available) and the price right-aligned in an 80px slot.
 * The hairline sits between rows (`ItemCardSeparator`), not on them, so the
 * hover surface can bleed past the content edge with rounded corners.
 */
export const ItemCard = React.memo<ItemCardProps>(
  ({ item, onPress, compact = false, square = false, viewCount, style }) => {
    const { t } = useTranslation();
    const img = item.image_urls && item.image_urls.length > 0 ? item.image_urls[0] : null;

    const sizeLabel = item.size ? `${t('components.itemCard.size')} ${item.size}` : null;

    if (compact) {
      const conditionLabel = t(`enums.condition.${item.condition}`, { defaultValue: item.condition });
      const compactMeta = [
        sizeLabel,
        conditionLabel,
        viewCount != null ? t('components.itemCard.views', { count: viewCount }) : null,
      ]
        .filter(Boolean)
        .join(META_SEPARATOR);

      const tint = item.status === 'reserved' ? StatusTints.primary : StatusTints.neutral;

      return (
        <Pressable
          onPress={onPress}
          accessibilityRole="button"
          accessibilityLabel={`${item.title}, ${price(item.price)}`}
          style={({ hovered }: any) => [
            styles.compactRow,
            hovered && styles.compactRowHovered,
            style,
          ]}
        >
          <View style={styles.compactThumb}>
            {img ? (
              <Image
                source={{ uri: imageService.getThumbnailUrl(img) }}
                style={styles.fill}
                contentFit="cover"
                transition={200}
                cachePolicy="memory-disk"
              />
            ) : (
              <Ionicons name="image-outline" size={20} color={Colors.border} />
            )}
          </View>

          <View style={styles.compactContent}>
            <Text style={[Typography.cardTitle, styles.title]} numberOfLines={1}>{item.title}</Text>
            <Text style={[Typography.meta, styles.metaText]} numberOfLines={1}>{compactMeta}</Text>
          </View>

          {item.status === 'available' ? (
            <ConditionBadge condition={item.condition} compact style={styles.chip} />
          ) : (
            <View style={[styles.statusChip, { backgroundColor: tint.bg }]}>
              <Text style={[styles.statusChipText, { color: tint.fg }]} numberOfLines={1}>
                {t(`enums.status.${item.status}`, { defaultValue: item.status })}
              </Text>
            </View>
          )}

          <Text style={[Typography.price, styles.compactPrice]} numberOfLines={1}>{price(item.price)}</Text>
        </Pressable>
      );
    }

    const gridMeta = [
      sizeLabel,
      item.club?.name ?? null,
      item.created_at ? formatRelativeTime(item.created_at, t) : null,
    ]
      .filter(Boolean)
      .join(META_SEPARATOR);

    return (
      <Pressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={`${item.title}, ${price(item.price)}`}
        style={[styles.gridCard, style]}
      >
        {({ hovered, pressed }: any) => (
          <>
            <View
              style={[
                styles.photo,
                square ? styles.photoSquare : styles.photoLandscape,
                hovered && styles.photoHovered,
                pressed && styles.pressed,
              ]}
            >
              {img ? (
                <Image
                  source={{ uri: imageService.getThumbnailUrl(img) }}
                  style={styles.fill}
                  contentFit="cover"
                  transition={200}
                  cachePolicy="memory-disk"
                />
              ) : (
                <Ionicons name="image-outline" size={28} color={Colors.border} />
              )}
            </View>

            <Text
              style={[Typography.cardTitle, styles.title, square ? styles.titleSquare : styles.titleGrid]}
              numberOfLines={1}
            >
              {item.title}
            </Text>
            {gridMeta ? (
              <Text style={[Typography.meta, styles.metaText, styles.gridMeta]} numberOfLines={1}>
                {gridMeta}
              </Text>
            ) : null}

            <View style={styles.gridFooter}>
              <Text style={[Typography.price, styles.price]} numberOfLines={1}>{price(item.price)}</Text>
              <ConditionBadge condition={item.condition} compact={square} style={styles.chip} />
            </View>
          </>
        )}
      </Pressable>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison for better memoization
    return (
      prevProps.item.id === nextProps.item.id &&
      prevProps.item.status === nextProps.item.status &&
      prevProps.item.price === nextProps.item.price &&
      prevProps.item.title === nextProps.item.title &&
      prevProps.item.condition === nextProps.item.condition &&
      // Everything else the card renders: the meta line and the thumbnail
      prevProps.item.size === nextProps.item.size &&
      prevProps.item.club?.name === nextProps.item.club?.name &&
      prevProps.item.created_at === nextProps.item.created_at &&
      prevProps.item.image_urls?.[0] === nextProps.item.image_urls?.[0] &&
      prevProps.compact === nextProps.compact &&
      prevProps.square === nextProps.square &&
      prevProps.viewCount === nextProps.viewCount &&
      prevProps.style === nextProps.style
    );
  }
);

/** Hairline between compact rows. */
export const ItemCardSeparator: React.FC = () => <View style={styles.separator} />;

const styles = StyleSheet.create({
  fill: {
    width: '100%',
    height: '100%',
  },
  pressed: {
    opacity: 0.9,
  },

  // ── Grid card: naked, only the photo has a shape ──────────────────────────
  gridCard: {
    width: '100%',
    // Allow vertical scrolling on mobile web while still detecting taps
    ...(Platform.OS === 'web' && { cursor: 'pointer', touchAction: 'pan-y' } as any),
  },
  photo: {
    width: '100%',
    backgroundColor: Colors.card,
    borderRadius: Radii.card,
    overflow: 'hidden',
    justifyContent: 'center',
    alignItems: 'center',
    ...(Platform.OS === 'web' && { transitionDuration: '200ms' } as any),
  },
  photoLandscape: {
    aspectRatio: 4 / 3,
  },
  photoSquare: {
    aspectRatio: 1,
  },
  photoHovered: {
    transform: [{ translateY: -2 }],
    ...Shadows.cardHover,
  },
  titleGrid: {
    marginTop: 10,
  },
  titleSquare: {
    marginTop: Spacing.sm,
  },
  gridMeta: {
    marginTop: 2,
  },
  gridFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: Spacing.sm,
    marginTop: 6,
  },

  // ── Compact: hairline row ─────────────────────────────────────────────────
  // Pulled out by its own horizontal padding: thumbnail and text stay on the
  // content edge while the hover surface gets air around them.
  compactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingVertical: COMPACT_PADDING_VERTICAL,
    paddingHorizontal: Spacing.md,
    marginHorizontal: -Spacing.md,
    borderRadius: Radii.card,
    ...(Platform.OS === 'web' && { cursor: 'pointer', touchAction: 'pan-y', transitionDuration: '150ms' } as any),
  },
  compactRowHovered: {
    backgroundColor: Colors.background,
  },
  separator: {
    height: ITEM_CARD_SEPARATOR_HEIGHT,
    backgroundColor: Colors.borderLight,
  },
  compactThumb: {
    width: COMPACT_THUMB,
    height: COMPACT_THUMB,
    borderRadius: Radii.thumb,
    overflow: 'hidden',
    backgroundColor: Colors.card,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  compactContent: {
    flex: 1,
    minWidth: 0,
  },
  compactPrice: {
    width: COMPACT_PRICE_SLOT,
    textAlign: 'right',
    color: Colors.textDark,
    flexShrink: 0,
  },
  chip: {
    alignSelf: 'center',
    flexShrink: 0,
  },
  statusChip: {
    alignSelf: 'center',
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: Radii.tag,
    flexShrink: 0,
  },
  statusChipText: {
    fontFamily: Fonts.semiBold,
    fontSize: 10,
    lineHeight: 13,
  },

  // ── Shared text ───────────────────────────────────────────────────────────
  title: {
    color: Colors.text,
  },
  price: {
    color: Colors.textDark,
    flexShrink: 0,
  },
  metaText: {
    color: Colors.textLight,
    fontFamily: Fonts.regular,
  },
});
